import React, { useState, useRef } from "react";
import {
  Box,
  Typography,
  IconButton,
  Dialog,
  DialogContent,
  Button,
  CircularProgress,
} from "@mui/material";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import FolderIcon from "@mui/icons-material/Folder";
import axios from "axios";
import { connect } from "react-redux";
import { uploadCustomTemplateApi as uploadCustomTemplateApiAction } from "../../redux/slices/postsSlice";

const UploadTemplateBox = ({ company, uploadCustomTemplateApi }) => {
  const [open, setOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);

  const handleOpen = () => setOpen(true);
  const handleClose = () => {
    setOpen(false);
    setUploading(false);
    setError(null);
  };

  const handleFileUpload = async (file) => {
    if (!file) return;

    let randomId = Math.random();
    const formData = new FormData();
    formData.append("image", file);
    formData.append("company_id", company?.id);
    formData.append("random_id", randomId);

    try {
      setUploading(true);
      setError(null);

      // TODO: Replace with your actual API endpoint
      const response = await uploadCustomTemplateApi(formData).unwrap();
      console.log("Upload Success:", response);
      setUploading(false);
      handleClose(); // Close modal on success
    } catch (err) {
      console.error("Upload Error:", err);
      setError("Failed to upload. Please try again.");
      setUploading(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    handleFileUpload(file);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const file = e.dataTransfer.files?.[0];
    handleFileUpload(file);
  };

  return (
    <>
      <Box
        onClick={handleOpen}
        sx={{
          border: "2px dashed #B388FF",
          borderRadius: "10px",
          backgroundColor: "#F3EFFF",
          padding: "40px",
          textAlign: "center",
          width: "250px",
          height: "15rem",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          cursor: "pointer",
          transition: "all 0.3s ease-in-out",
          "&:hover": {
            backgroundColor: "#EADFFF",
          },
          mb: 2,
        }}
      >
        <IconButton
          sx={{
            backgroundColor: "#B388FF",
            color: "#fff",
            mb: 2,
            "&:hover": {
              backgroundColor: "#9F6EFF",
            },
          }}
          size="large"
        >
          <UploadFileIcon fontSize="large" />
        </IconButton>
        <Typography fontFamily="Outfit" fontWeight="600" color="#2A3244">
          Upload template
        </Typography>
        <Typography color="#69748B" fontFamily="Outfit" variant="body2">
          PNG to JPG files
        </Typography>
      </Box>

      {/* Modal */}
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        sx={{
          "& .MuiDialog-paper": {
            margin: 0,
            width: { xs: "95vw", md: "30vw" },
            borderRadius: "10px",
          },
        }}
      >
        <DialogContent
          sx={{
            textAlign: "center",
            p: 4,
            backgroundColor: "#fff",
          }}
        >
          <Typography variant="h6" fontWeight="bold" mb={2} fontFamily="Outfit">
            Upload Custom Template
          </Typography>

          <Box
            onDrop={handleDrop}
            onDragOver={(e) => e.preventDefault()}
            sx={{
              border: "2px dashed #D3D3D3",
              borderRadius: "10px",
              padding: "30px",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              mb: 2,
            }}
          >
            <FolderIcon sx={{ fontSize: 48, color: "#F5A623", mb: 1 }} />
            <Typography fontWeight={500} fontFamily="Outfit">
              Drag and drop your template here
            </Typography>
            <Typography fontFamily="Outfit" my={1}>
              or
            </Typography>

            <Button
              onClick={() => fileInputRef.current.click()}
              variant="contained"
              sx={{
                backgroundColor: "#7C4DFF",
                textTransform: "none",
                px: 3,
                py: 1,
                fontFamily: "Outfit",
                borderRadius: "8px",
                fontWeight: "bold",
                "&:hover": {
                  backgroundColor: "#651FFF",
                },
              }}
            >
              Choose file +
            </Button>

            <input
              ref={fileInputRef}
              type="file"
              accept="image/png, image/jpeg"
              style={{ display: "none" }}
              onChange={handleFileChange}
            />

            <Typography mt={2} fontSize="12px" color="#888" fontFamily="Outfit">
              Supported formats: PNG, JPG (Max 10MB) <br />
              Will be auto-resized for LinkedIn compatibility
            </Typography>
          </Box>

          {uploading && <CircularProgress />}
          {error && (
            <Typography mt={2} color="error" fontFamily="Outfit">
              {error}
            </Typography>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

const mapStateToProps = (state) => ({
  company: state.company.company,
});

const mapDispatchToProps = {
  uploadCustomTemplateApi: uploadCustomTemplateApiAction,
};

export default connect(mapStateToProps, mapDispatchToProps)(UploadTemplateBox);
